import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, Zap, Shield, ChevronRight, Sparkles, Terminal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.6,
    ease: [0.16, 1, 0.3, 1]
  }
};
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
export const Home = () => {
  return <div className="min-h-screen" data-id="element-219">
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden" data-id="element-220">
        {/* Background Effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-lab-violet/20 rounded-full blur-[120px] opacity-50 pointer-events-none" data-id="element-221" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-lab-blue/10 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-222" />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] pointer-events-none" data-id="element-223" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-224">
          <motion.div className="flex flex-col items-center text-center max-w-4xl mx-auto" initial="initial" animate="animate" variants={staggerContainer} data-id="element-225">
            <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-panel border-lab-violetLight/30 mb-8" data-id="element-226">
              <span className="w-2 h-2 rounded-full bg-lab-violetLight animate-pulse-slow shadow-[0_0_10px_rgba(139,92,246,0.8)]" data-id="element-227"></span>
              <span className="text-xs font-mono text-lab-neon tracking-wider uppercase" data-id="element-228">
                CLVN-LRR OS v2.0 Online
              </span>
            </motion.div>

            <motion.h1 variants={fadeIn} className="text-5xl md:text-7xl lg:text-8xl font-display font-bold tracking-tight mb-6 leading-[1.1]" data-id="element-229">
              Expansión Cognitiva <br className="hidden md:block" data-id="element-230" />
              <span className="text-gradient" data-id="element-231">Sistemas Avanzados.</span>
            </motion.h1>

            <motion.p variants={fadeIn} className="text-lg md:text-xl text-lab-muted max-w-2xl mb-10 font-light leading-relaxed" data-id="element-232">
              Laboratorio digital premium para la optimización de la mente.
              Herramientas, prompts y arquitecturas de inteligencia artificial
              diseñadas para el 1% que construye el futuro.
            </motion.p>

            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto" data-id="element-233">
              <Link to="/catalog" className="w-full sm:w-auto" data-id="element-234">
                <Button variant="primary" size="lg" icon={ArrowRight} fullWidth data-id="element-235">
                  Acceder al Laboratorio
                </Button>
              </Link>
              <Link to="/about" className="w-full sm:w-auto" data-id="element-236">
                <Button variant="outline" size="lg" fullWidth data-id="element-237">
                  Leer la Tesis
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* AUTHORITY LOGOS */}
      <section className="py-10 border-y border-white/[0.02] bg-white/[0.01]" data-id="element-238">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-239">
          <p className="text-center text-xs font-mono text-lab-muted uppercase tracking-widest mb-8" data-id="element-240">
            Sistemas utilizados por mentes en
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-40 grayscale" data-id="element-241">
            {/* Placeholder logos for startup vibe */}
            <div className="text-xl font-display font-bold" data-id="element-242">OpenAI</div>
            <div className="text-xl font-display font-bold" data-id="element-243">Neuralink</div>
            <div className="text-xl font-display font-bold" data-id="element-244">Tesla</div>
            <div className="text-xl font-display font-bold" data-id="element-245">SpaceX</div>
            <div className="text-xl font-display font-bold" data-id="element-246">YCombinator</div>
          </div>
        </div>
      </section>

      {/* FEATURES / ARCHITECTURE */}
      <section className="py-24 md:py-32 relative" data-id="element-247">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-248">
          <div className="mb-16 md:mb-24" data-id="element-249">
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-4" data-id="element-250">
              Arquitectura de{' '}
              <span className="text-gradient-violet" data-id="element-251">Productividad</span>
            </h2>
            <p className="text-lab-muted text-lg max-w-2xl" data-id="element-252">
              No vendemos cursos. Diseñamos sistemas operativos para tu cerebro.
              Cada producto es una actualización de software para tu mente.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-253">
            {[{
            icon: Brain,
            title: 'Neuro-Optimización',
            desc: 'Frameworks mentales basados en neurociencia para maximizar enfoque, retención y procesamiento de información compleja.'
          }, {
            icon: Terminal,
            title: 'Inteligencia Aplicada',
            desc: 'Librerías de prompts de grado ingeniería y automatizaciones para delegar el 80% del trabajo cognitivo a la IA.'
          }, {
            icon: Shield,
            title: 'Sistemas Cerrados',
            desc: 'Arquitecturas de conocimiento exclusivas. Metodologías probadas en entornos de alta presión y startups unicornio.'
          }].map((feature, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="glass-card p-8 rounded-2xl group" data-id="element-254">
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-6 border border-white/10 group-hover:border-lab-violetLight/50 group-hover:bg-lab-violet/10 transition-colors" data-id="element-255">
                  <feature.icon className="text-lab-neon" size={24} data-id="element-256" />
                </div>
                <h3 className="text-xl font-display font-semibold mb-3 text-white" data-id="element-257">
                  {feature.title}
                </h3>
                <p className="text-lab-muted leading-relaxed text-sm" data-id="element-258">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-24 bg-lab-dark relative border-t border-white/[0.02]" data-id="element-259">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-260">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6" data-id="element-261">
            <div data-id="element-262">
              <h2 className="text-3xl md:text-5xl font-display font-bold mb-4" data-id="element-263">
                Sistemas <span className="text-gradient" data-id="element-264">Destacados</span>
              </h2>
              <p className="text-lab-muted text-lg max-w-xl" data-id="element-265">
                Herramientas digitales premium diseñadas para expansión
                inmediata.
              </p>
            </div>
            <Link to="/catalog" data-id="element-266">
              <Button variant="ghost" icon={ChevronRight} data-id="element-267">
                Ver todo el catálogo
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-268">
            {/* Product 1 */}
            <Link to="/product/1" className="group block" data-id="element-269">
              <div className="glass-card rounded-3xl overflow-hidden relative aspect-[4/3] md:aspect-[16/9]" data-id="element-270">
                <div className="absolute inset-0 bg-gradient-to-br from-lab-violet/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" data-id="element-271" />
                <div className="absolute top-6 left-6 z-20 flex gap-2" data-id="element-272">
                  <span className="px-3 py-1 text-xs font-mono bg-white text-black rounded-full font-medium" data-id="element-273">
                    Bestseller
                  </span>
                  <span className="px-3 py-1 text-xs font-mono glass-panel rounded-full text-white" data-id="element-274">
                    v2.4
                  </span>
                </div>

                {/* Abstract Product Visual */}
                <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0f]" data-id="element-275">
                  <div className="w-48 h-48 rounded-full border border-lab-violet/30 flex items-center justify-center relative group-hover:scale-105 transition-transform duration-700" data-id="element-276">
                    <div className="w-32 h-32 rounded-full border border-lab-blueLight/30 flex items-center justify-center animate-[spin_10s_linear_infinite]" data-id="element-277">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-lab-violet to-lab-blue blur-xl opacity-50" data-id="element-278" />
                    </div>
                    <Brain className="absolute text-white/80" size={40} data-id="element-279" />
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 bg-gradient-to-t from-black/90 via-black/50 to-transparent z-20" data-id="element-280">
                  <h3 className="text-2xl font-display font-bold text-white mb-2 group-hover:text-lab-neon transition-colors" data-id="element-281">
                    Neural OS: Sistema de Segundo Cerebro
                  </h3>
                  <div className="flex items-center justify-between" data-id="element-282">
                    <p className="text-lab-muted text-sm" data-id="element-283">
                      Plantilla Notion + Framework Cognitivo
                    </p>
                    <p className="font-mono text-white font-medium" data-id="element-284">$149 USD</p>
                  </div>
                </div>
              </div>
            </Link>

            {/* Product 2 */}
            <Link to="/product/2" className="group block" data-id="element-285">
              <div className="glass-card rounded-3xl overflow-hidden relative aspect-[4/3] md:aspect-[16/9]" data-id="element-286">
                <div className="absolute inset-0 bg-gradient-to-br from-lab-blue/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" data-id="element-287" />
                <div className="absolute top-6 left-6 z-20 flex gap-2" data-id="element-288">
                  <span className="px-3 py-1 text-xs font-mono glass-panel rounded-full text-white" data-id="element-289">
                    Nuevo
                  </span>
                </div>

                {/* Abstract Product Visual */}
                <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0f]" data-id="element-290">
                  <div className="w-48 h-48 border border-lab-blue/30 rotate-45 flex items-center justify-center relative group-hover:scale-105 transition-transform duration-700" data-id="element-291">
                    <div className="w-32 h-32 border border-lab-neon/30 -rotate-45 flex items-center justify-center animate-[pulse_4s_ease-in-out_infinite]" data-id="element-292">
                      <div className="w-16 h-16 bg-gradient-to-tr from-lab-blue to-lab-neon blur-xl opacity-50" data-id="element-293" />
                    </div>
                    <Terminal className="absolute text-white/80 -rotate-45" size={40} data-id="element-294" />
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 bg-gradient-to-t from-black/90 via-black/50 to-transparent z-20" data-id="element-295">
                  <h3 className="text-2xl font-display font-bold text-white mb-2 group-hover:text-lab-blueLight transition-colors" data-id="element-296">
                    Prompt Engineering Masterclass
                  </h3>
                  <div className="flex items-center justify-between" data-id="element-297">
                    <p className="text-lab-muted text-sm" data-id="element-298">
                      Curso + Librería de 500+ Prompts
                    </p>
                    <p className="font-mono text-white font-medium" data-id="element-299">$299 USD</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CONVERSION / CTA */}
      <section className="py-32 relative overflow-hidden" data-id="element-300">
        <div className="absolute inset-0 bg-lab-violet/5" data-id="element-301" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[400px] bg-gradient-to-r from-lab-violet/20 to-lab-blue/20 blur-[100px] pointer-events-none" data-id="element-302" />

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-303">
          <Sparkles className="mx-auto text-lab-neon mb-8" size={32} data-id="element-304" />
          <h2 className="text-4xl md:text-6xl font-display font-bold mb-6" data-id="element-305">
            Eleva tu <span className="text-gradient" data-id="element-306">Ancho de Banda</span>{' '}
            Mental
          </h2>
          <p className="text-xl text-lab-muted mb-10 font-light" data-id="element-307">
            El conocimiento común produce resultados comunes. Accede a los
            sistemas operativos de la élite digital.
          </p>
          <Link to="/catalog" data-id="element-308">
            <Button variant="primary" size="lg" icon={Zap} data-id="element-309">
              Iniciar Optimización
            </Button>
          </Link>
          <p className="mt-6 text-sm text-lab-muted font-mono uppercase tracking-widest" data-id="element-310">
            Acceso Inmediato • Garantía de 30 Días
          </p>
        </div>
      </section>
    </div>;
};